import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api/apiClient';

export interface PurchaseOrder {
  _id: string;
  customerId: {
    _id: string;
    companyName: string;
    gst: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    pinCode?: string;
    email: string;
    mobile: string;
  } | string;
  companyId: {
      gst: string;
    _id: string;
    name: string;
  } | string;
  poNumber: string;
  poDateTime: string;
  jobType?: string;
  punching: boolean;
  bending: boolean;
  welding: boolean;
  powder: boolean;
  assembly: boolean;
  isActive: boolean;
  isDeleted: boolean;
  remarks?: string;
  createdBy?: {
    _id: string;
    name?: string;
    empId?: string;
    email?: string;
    mobile?: string;
  };
  updatedBy?: {
    _id: string;
    name?: string;
    empId?: string;
    email?: string;
    mobile?: string;
  };
  createdAt: string;
  updatedAt: string;
  currentProcess: string;
  currentLevel: string;
  processStatus: string;
}

export interface PurchaseOrderFormData {
  customerId: string;
  companyId: string;
  poNumber: string;
  poDateTime: string;
  punching: boolean;
  bending: boolean;
  welding: boolean;
  powder: boolean;
  assembly: boolean;
  isActive?: boolean;
  remarks?: string;
  level?:number,
  jobType?:string

}

export interface PurchaseOrderListParams {
  page?: number;
  limit?: number;
  isActive?: boolean;
  customerId?: string;
}

export interface PurchaseOrderListResponse {
  success: boolean;
  message: string;
  data: {
    docs: PurchaseOrder[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  };
}

export interface PurchaseOrderResponse {
  success: boolean;
  message: string;
  data: PurchaseOrder;
}

class PurchaseOrderService {
  private baseUrl = '/purchase-orders';

  async getPurchaseOrders(params: PurchaseOrderListParams = {}): Promise<PurchaseOrderListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.customerId) queryParams.append('customerId', params.customerId);

    const url = `${this.baseUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiGet<PurchaseOrderListResponse>(url);
  }

  async createPurchaseOrder(data: PurchaseOrderFormData): Promise<PurchaseOrderResponse> {
    return apiPost<PurchaseOrderResponse>(this.baseUrl, data);
  }

  async updatePurchaseOrder(id: string, data: PurchaseOrderFormData): Promise<PurchaseOrderResponse> {
    return apiPut<PurchaseOrderResponse>(`${this.baseUrl}/${id}`, data);
  }

  async deletePurchaseOrder(id: string): Promise<{ success: boolean; message: string }> {
    return apiDelete<{ success: boolean; message: string }>(`${this.baseUrl}/${id}`);
  }

  async getPurchaseOrderById(id: string): Promise<PurchaseOrderResponse> {
    return apiGet<PurchaseOrderResponse>(`${this.baseUrl}/${id}`);
  }

  async getComprehensivePurchaseOrder(id: string): Promise<any> {
    return apiGet<any>(`${this.baseUrl}/${id}/comprehensive`);
  }

  async getInvoicesByPurchaseOrder(purchaseOrderId: string): Promise<any> {
    return apiGet<any>(`/invoices/purchase-order/${purchaseOrderId}`);
  }

  async createInvoice(data: any): Promise<any> {
    return apiPost<any>('/invoices', data);
  }

  async updateInvoice(invoiceId: string, data: any): Promise<any> {
    return apiPut<any>(`/invoices/${invoiceId}`, data);
  }
}

export const purchaseOrderService = new PurchaseOrderService();